import React, { useRef, useEffect } from "react";
import { Box, Link, Text, Flex, HStack } from "@chakra-ui/react";
import LogoHeader from "../atoms/Logo";
import { BsLinkedin, BsInstagram, BsEnvelope } from "react-icons/bs";

const Logo = () => {
  return <LogoHeader />;
};

export default function Footer() {
  const footerRef = useRef(null);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (footerRef.current) {
        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const clientY = event.touches ? event.touches[0].clientY : event.clientY;
        const { offsetWidth, offsetHeight } = footerRef.current;
        const x = (clientX / offsetWidth) * 100;
        const y = (clientY / offsetHeight) * 100;

        // Create grainy noise pattern using CSS
        const noiseIntensity = 7.4 + (Math.sin(Date.now() * 0.005) + 1) * 0.05;
        const grainPattern = `
          radial-gradient(circle at ${x}% ${y}%, #115945, #072B21),
          repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, ${noiseIntensity * 0.01}),
            rgba(0, 0, 0, ${noiseIntensity * 0.01}) 1px,
            transparent 1px,
            transparent 30px
          ),
          repeating-linear-gradient(
            90deg,
            rgba(0, 0, 0, ${noiseIntensity * 0.01}),
            rgba(0, 0, 0, ${noiseIntensity * 0.01}) 1px,
            transparent 1px,
            transparent 30px
          ),
          repeating-linear-gradient(
            45deg,
            rgba(0, 0, 0, ${noiseIntensity * 0.005}),
            rgba(0, 0, 0, ${noiseIntensity * 0.005}) 1px,
            transparent 1px,
            transparent 40px
          ),
          repeating-linear-gradient(
            -45deg,
            rgba(0, 0, 0, ${noiseIntensity * 0.005}),
            rgba(0, 0, 0, ${noiseIntensity * 0.005}) 1px,
            transparent 1px,
            transparent 40px
          )
        `;

        footerRef.current.style.background = grainPattern;
        footerRef.current.style.backgroundBlendMode =
          "overlay, multiply, multiply, multiply, multiply";
      }
    };

    // Animate the grain effect
    const animateGrain = () => {
      if (footerRef.current) {
        const currentStyle = footerRef.current.style.background;
        if (currentStyle.includes("radial-gradient")) {
          // Trigger a re-render of the grain effect
          footerRef.current.style.background = currentStyle;
        }
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    const grainInterval = setInterval(animateGrain, 50);

    // Initialize background effect at center for mobile
    const fakeEvent = {
      clientX: window.innerWidth / 2,
      clientY: window.innerHeight / 2,
    };
    handlePointerMove(fakeEvent);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      clearInterval(grainInterval);
    };
  }, []);

  return (
    <Box
      ref={footerRef}
      as="footer"
      id="footer"
      w="full"
      color="white"
      px={{ base: "24px", md: "60px", lg: "60px" }}
      py={{ base: "60px", md: "60px" }}
      // pb={{ base: "0", md: "80px" }}
      h="auto"
      position="relative"
      height={"100%"}
      alignContent={"center"}
      display={"flex"}
    >
      {/* Content */}
      <Flex
        direction="column"
        alignItems="start"
        position="relative"
        zIndex={10}
        maxW="7xl"
        w={"100%"}
        p={{ base: 6, md: 6 }}
        rounded={"15px"}
        mx="auto"
        backdropFilter={"blur(10px)"}
      >
        <Box pb={{ base: "40px", md: "10px" }}>
          <Logo />
        </Box>

        <Box display={{ base: "block", md: "none" }} h="1px" w="full" bg="whiteAlpha.400" />

        {/* Links  */}
        <Flex justify="space-between" w="full" alignItems="center" flexWrap="wrap">
          <Flex
            direction={{ base: "column", md: "row" }}
            py={{ base: "40px", md: "24px" }}
            gap="24px"
            opacity={0.8}
          >
            <Link href="#aboutme">About Me</Link>
            <Link href="#journey">My Journey</Link>
            <Link href="#moreabout">More About</Link>
            <Link href="#contact">Contact</Link>
          </Flex>

          <HStack display={{ base: "none", md: "flex" }} gap="32px">
            <Link href="https://www.linkedin.com/in/nathanmls/" isExternal>
              <BsLinkedin color="white" size={20} />
            </Link>
          </HStack>
        </Flex>

        <Box h="1px" w="full" bg="whiteAlpha.400" />

        <HStack display={{ base: "flex", md: "none" }} gap="32px" mt="46px">
          <Link href="https://www.linkedin.com/in/nathanmls/" isExternal>
            <BsLinkedin color="white" size={20} />
          </Link>
        </HStack>

        <Flex
          mt={{ base: "40px", md: "32px" }}
          opacity={{ base: 0.5, md: 0.9 }}
          direction={{ base: "column-reverse", sm: "row" }}
          justify="space-between"
          w="full"
          gap="24px"
        >
          <Text fontSize="sm">© 2026 Nathan Martins. All rights reserved</Text>
          <HStack gap="24px" fontSize="sm">
            <Link display={"flex"} gap={3} href="https://www.linkedin.com/in/nathanmls/" isExternal>
              <BsInstagram color="white" size={20} />
              Instagram
            </Link>
            <Link display={"flex"} gap={3} href="mailto:nathanmls@hotmail.com">
              <BsEnvelope color="white" size={20} />
              Email
            </Link>
          </HStack>
        </Flex>
      </Flex>
    </Box>
  );
}
